import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Chip, Input, Textarea } from '../components/ui';
import { ToneSlider } from '../components/tier1/ToneSlider';
import { useBriefState, type Tier1Inputs } from '../lib/brief-state';

const GENRES = [
  { value: 'fantasy', label: 'Fantasy' },
  { value: 'historical', label: 'Historical' },
  { value: 'horror', label: 'Horror' },
  { value: 'literary-speculative', label: 'Literary Speculative' },
  { value: 'literary-thriller', label: 'Literary Thriller' },
  { value: 'mystery', label: 'Mystery' },
  { value: 'romance', label: 'Romance' },
  { value: 'romantasy', label: 'Romantasy' },
  { value: 'sci-fi', label: 'Science Fiction' },
  { value: 'ya', label: 'Young Adult' },
] as const;

const EMOTIONAL_CORES = [
  'wonder',
  'dread',
  'longing',
  'rage',
  'tenderness',
  'obsession',
  'grief',
  'euphoria',
  'defiance',
  'melancholy',
] as const;

export default function BriefNew() {
  const navigate = useNavigate();
  const { startStream } = useBriefState();

  const [genre, setGenre] = useState('');
  const [emotionalCore, setEmotionalCore] = useState('');
  const [compTitles, setCompTitles] = useState('');
  const [scene, setScene] = useState('');
  const [toneDarkness, setToneDarkness] = useState<1 | 2 | 3 | 4 | 5>(3);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = genre && emotionalCore && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    const inputs: Tier1Inputs = {
      genre,
      emotionalCore,
      compTitles,
      scene,
      toneDarkness,
    };

    startStream(inputs);
    navigate('/brief/new-session');
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border px-5 sm:px-8 py-4">
        <div className="max-w-container mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-heading">
            <span className="font-display text-lg">Ghostwright</span>
            <span className="text-primary leading-none" aria-hidden="true">·</span>
          </a>
          <span className="text-sm text-muted-foreground">New Brief</span>
        </div>
      </header>

      <main className="px-5 sm:px-8 py-12 sm:py-16">
        <div className="max-w-[640px] mx-auto space-y-10 sm:space-y-12 snap-y snap-mandatory sm:snap-none">

          <section className="snap-start">
            <label className="block text-sm font-medium text-foreground mb-3">
              Genre
            </label>
            <div className="flex flex-wrap gap-2">
              {GENRES.map((g) => (
                <Chip
                  key={g.value}
                  selected={genre === g.value}
                  onClick={() => setGenre(g.value)}
                >
                  {g.label}
                </Chip>
              ))}
            </div>
          </section>

          <section className="snap-start">
            <label className="block text-sm font-medium text-foreground mb-3">
              Emotional Core
            </label>
            <div className="flex flex-wrap gap-2">
              {EMOTIONAL_CORES.map((ec) => (
                <Chip
                  key={ec}
                  selected={emotionalCore === ec}
                  onClick={() => setEmotionalCore(ec)}
                >
                  {ec.charAt(0).toUpperCase() + ec.slice(1)}
                </Chip>
              ))}
            </div>
          </section>

          <section className="snap-start">
            <label htmlFor="comp-titles" className="block text-sm font-medium text-foreground mb-3">
              Comp Titles
            </label>
            <Input
              id="comp-titles"
              value={compTitles}
              onChange={(e) => setCompTitles(e.target.value)}
              placeholder="e.g. Name of the Wind meets The Night Circus"
            />
          </section>

          <section className="snap-start">
            <label htmlFor="opening-scene" className="block text-sm font-medium text-foreground mb-3">
              Opening Scene
            </label>
            <Textarea
              id="opening-scene"
              value={scene}
              onChange={(e) => {
                if (e.target.value.length <= 500) setScene(e.target.value);
              }}
              placeholder="Describe a scene that captures the mood..."
              rows={4}
            />
            <p className="text-xs text-muted-foreground mt-1.5 text-right">
              {scene.length}/500
            </p>
          </section>

          <section className="snap-start">
            <label className="block text-sm font-medium text-foreground mb-3">
              Tone Darkness
            </label>
            <ToneSlider value={toneDarkness} onChange={setToneDarkness} />
          </section>

          <div className="pt-4">
            {error && (
              <p className="text-sm text-destructive mb-4">{error}</p>
            )}
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full sm:w-auto"
            >
              {submitting ? 'Generating...' : 'Generate Brief'}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
